import { Paper, Stack, Typography } from '@mui/material';
import dayjs from 'dayjs';
import React, { useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import SkeletonLoader from '@components/SkeletonLoader';

import { useHealthChart } from '@hooks/useHealthChart';
import { useSession } from '@hooks/useSession';

import { useGetAlertStats } from 'generated/alert.generated';
import { AlertStatus } from 'generated/types';

import { FontWeight } from '../../../../../../theme/typography';
import ClearAlertsButton from './ClearAlerts';

const messages = defineMessages({
  alerts: {
    id: 'healthTracker.detail.header.alerts.title',
    defaultMessage: 'Alerts: ',
  },
  lastUpdate: {
    id: 'healthTracker.detail.header.lastUpdate',
    defaultMessage: 'Last Update: ',
  },
  count: {
    id: 'healthTracker.detail.header.alerts.count',
    defaultMessage: '{count} Open',
  },
  latest: {
    id: 'healthTracker.detail.header.alerts.lastUpdate',
    defaultMessage: '{number} {interval} ago',
  },
});

export default function Alerts() {
  const { formatMessage } = useIntl();
  const {
    state: { activeProfile },
  } = useSession();
  const { category, code, showAlerts } = useHealthChart();
  const [vitall_patient_id, account_id] = [
    activeProfile?.vitall_patient_id ?? '',
    activeProfile?.account_id ?? '',
  ];
  const { data, loading } = useGetAlertStats({
    variables: {
      input: {
        vitall_patient_id,
        account_id,
        code,
        statuses: [AlertStatus.open],
        category,
      },
    },
    skip: !vitall_patient_id || !account_id || !showAlerts,
  });

  const lastUpdate = useMemo(() => {
    const last_update = data?.getAlertStats.last_update;
    const minuteDiff = dayjs().diff(last_update, 'minutes');
    if (minuteDiff < 60) {
      return {
        number: minuteDiff,
        interval: formatMessage(
          { id: `GLOBAL.dateInterval.minute` },
          { count: minuteDiff }
        ),
      };
    } else if (minuteDiff >= 60 * 24 * 365) {
      const yearDiff = dayjs().diff(last_update, 'years');
      return {
        number: yearDiff,
        interval: formatMessage(
          { id: `GLOBAL.dateInterval.year` },
          { count: yearDiff }
        ),
      };
    } else if (minuteDiff >= 60 * 24 * 30) {
      const monthDiff = dayjs().diff(last_update, 'months');
      return {
        number: monthDiff,
        interval: formatMessage(
          { id: `GLOBAL.dateInterval.month` },
          {
            count: monthDiff,
          }
        ),
      };
    } else if (minuteDiff >= 60 * 24) {
      const dayDiff = dayjs().diff(last_update, 'days');
      return {
        number: dayDiff,
        interval: formatMessage(
          { id: `GLOBAL.dateInterval.day` },
          {
            count: dayDiff,
          }
        ),
      };
    }
    // minuteDiff >= 60
    const hourDiff = dayjs().diff(last_update, 'hours');
    return {
      number: hourDiff,
      interval: formatMessage(
        { id: `GLOBAL.dateInterval.hour` },
        {
          count: hourDiff,
        }
      ),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.getAlertStats.last_update]);

  if (!showAlerts) {
    return null;
  }

  return (
    <SkeletonLoader loading={loading}>
      <Paper sx={{ width: 'fit-content', my: 1, p: 1 }}>
        <Stack direction="row" spacing={2}>
          <Stack direction="row">
            <Typography variant="caption">
              {formatMessage(messages.alerts)}
            </Typography>
            <Typography
              variant="caption"
              color="error"
              sx={{ fontWeight: FontWeight.Bold }}
            >
              {formatMessage(messages.count, {
                count: data?.getAlertStats.count ?? 0,
              })}
            </Typography>
          </Stack>

          <Stack direction="row">
            <Typography variant="caption">
              {formatMessage(messages.lastUpdate)}
            </Typography>
            <Typography
              variant="caption"
              sx={({ palette }) => ({
                fontWeight: FontWeight.Bold,
                color: palette.info.main,
              })}
            >
              {data?.getAlertStats.last_update
                ? formatMessage(messages.latest, {
                    ...lastUpdate,
                  })
                : '-'}
            </Typography>
          </Stack>

          <ClearAlertsButton disabled={!data?.getAlertStats.count} />
        </Stack>
      </Paper>
    </SkeletonLoader>
  );
}
